import axios from 'axios';
import { CollectionWithCursor, FavoritesData } from '@inplayer-org/inplayer.js';
import { Auth } from 'aws-amplify';
import { useConfigStore } from '../../stores/ConfigStore';
import { FormValidationError } from '../../errors/FormValidationError';
import { PATH_SIGN_IN } from '../../paths';
import type { RequestOptions } from '../../../types/account';
import { useAccountStore } from '../../stores/AccountStore';
import { useAppConfig } from '../../../../hooks-react/src/useAppConfig';
import { useAppInfo } from '../../../../hooks-react/src/useAppInfo';
import { getApolloClient } from '../../graphql/ApolloClient';
import { GET_USER_QUERY } from '../../graphql/query/auth.query';

const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};
const getJWTToken = () => {
  const token = localStorage.getItem('inplayer_token');
  if (token) {
    const parsedToken = JSON.parse(token);
    if (parsedToken?.token) {
      return parsedToken.token;
    }
  }
};

const getHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  Authorization: token ? `Bearer ${token}` : '',
});

const getBaseURL = (): string => {
  const { appConfig } = useConfigStore.getState();
  const { customBaseApiUrl } = useAppInfo();
  return customBaseApiUrl || appConfig?.api?.base_url || '';
};

const getEndpoint = (path: string | undefined): string | null => {
  const baseURL = getBaseURL();
  return baseURL && path ? `${baseURL}${path}` : null;
};

export const getUserProfile = async (): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const apolloClient = getApolloClient();

  try {
    const response: any = await apolloClient.query({
      query: GET_USER_QUERY,
      fetchPolicy: 'network-only',
    });

    if (response?.errors && response.errors.length > 0) {
      console.error('GraphQL errors fetching profile:', response.errors);
      return null;
    }

    const userRaw = (response?.data as any)?.getUser;
    if (!userRaw) return null;

    return {
      id: userRaw.uId,
      email: userRaw.uEmailId,
      firstName: userRaw.uFirstName,
      lastName: userRaw.uSurName,
      fullName: `${userRaw.uFirstName || ''} ${userRaw.uSurName || ''}`.trim() || userRaw.uFirstName,
      role: userRaw.uRole,
      uAvatarUrl: userRaw.uAvatarUrl,
      metadata: {
        avatarUrl: userRaw.uAvatarUrl,
        username: userRaw.uUserName,
        orgDetails: userRaw.orgDetails,
      }
    };
  } catch (error) {
    console.error('Error fetching GraphQL user profile:', error);
    return null;
  }
};

export const createUserProfile = async (payload: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.create_user_profile);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.post(endpoint, payload, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error creating user profile:', error);
    return null;
  }
};

export const getCustomFavorites = async (): Promise<CollectionWithCursor<FavoritesData>> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return { collection: [], cursor: '' };
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.favorite?.get_favorite);
  if (!endpoint) {
    console.error('API config not ready');
    return { collection: [], cursor: '' };
  }

  try {
    const response = await axios.get(endpoint, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error fetching favorites:', error);
    return { collection: [], cursor: '' };
  }
};

export const updateCustomFavorites = async (mediaId: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.favorite?.update_favorite);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const payload = new URLSearchParams();
  payload.append('media_id', mediaId);

  try {
    const response = await axios.post(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating favorite:', error);
    return null;
  }
};

export const deleteCustomFavorites = async (mediaId: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpointBase = getEndpoint(appConfig?.api?.end_point?.favorite?.remove_favorite);
  if (!endpointBase) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(`${endpointBase}/${mediaId}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting favorite media:', error);
    return null;
  }
};

export const getCustomWatchHistory = async (): Promise<any> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.watch_history?.get_watch_history);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.get(endpoint, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error fetching watch history:', error);
    return null;
  }
};

export const updateCustomWatchHistory = async (mediaId: string, progress: number): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.watch_history?.update_watch_history);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const payload = new URLSearchParams();
  payload.append('media_id', mediaId);
  payload.append('progress', progress?.toString() || '0');

  try {
    const response = await axios.post(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating watch history:', error);
    return null;
  }
};

export const deleteCustomWatchHistory = async (mediaId: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpointBase = getEndpoint(appConfig?.api?.end_point?.watch_history?.remove_watch_history);
  if (!endpointBase) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(`${endpointBase}/${mediaId}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting watch history:', error);
    return null;
  }
};

export const customDeleteAccount = async (): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.delete_account);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(endpoint, { headers: getHeaders(token) });
    return response;
  } catch (error) {
    console.error('Error deleting user profile:', error);
    return null;
  }
};

export const customChangePassword = async (oldPassword: string, password: string, passwordConfirmation: string) => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }
  const { isCustomApiEnabled } = useAppConfig();
  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.change_password);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const body = new URLSearchParams();
  body.append('password', password);
  body.append('password_confirmation', passwordConfirmation);
  body.append('old_password', oldPassword);

  try {
    const response = await axios.post(endpoint, body.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.status === 200) {
      if (isCustomApiEnabled) {
        cognitoLogout();
      }
    }

    return response;
  } catch (error: any) {
    console.error('Error changePassword', error);
    throw error?.response?.data?.message;
  }
};

export const cognitoLogout = async () => {
  await Auth.signOut();
  localStorage.clear();
  window.location.href = PATH_SIGN_IN;
};

const performRequest = async (path: string = '/', method = 'GET', body?: string, options: RequestOptions = {}) => {
  try {
    const token = getJWTToken();

    const resp = await fetch(`${path}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      keepalive: options.keepalive,
      method,
      body,
    });

    return await resp.json();
  } catch (error: unknown) {
    return {
      errors: Array.of(error as string),
    };
  }
};

const get = <T>(path: string, options?: RequestOptions) => performRequest(path, 'GET', undefined, options) as Promise<T>;

export const LoginByCode = async (QrCode: string) => {
  const { appConfig } = useConfigStore.getState();
  useAccountStore.setState({ loading: true });

  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.activate_tv);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const response: any = await get(`${endpoint}/${QrCode}`, { authenticate: true });

  if (!response.ok) {
    console.error('API request failed:', response);
    throw new FormValidationError({ form: [response.error] });
  }
  if (response.ok) {
    useAccountStore.setState({ loading: false });
  }
  return response;
};
